import { useState } from "react";
import { useAdminStore } from "../../../../store/useAdminStore";
import {
   Select,
   SelectContent,
   SelectItem,
   SelectTrigger,
   SelectValue,
} from "@/Components/ui/select";
import { Button } from "@/Components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/Components/ui/card";
import { Loader2, Trash2, Upload } from "lucide-react";
import { Input } from "@/Components/ui/input";
import { FolderType } from "@/types/productTypes";

// This component demonstrates image uploading and creation in the frontend.
export default function ImageUploader() {
   const [files, setFiles] = useState<File[]>([]);
   const [previews, setPreviews] = useState<string[]>([]);
   const [uploading, setUploading] = useState(false);
   const [selectedFolder, setSelectedFolder] = useState<string>("none");

   const { uploadImages, folders } = useAdminStore();
   const stageId = 1;

   const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
         const selectedFiles = Array.from(e.target.files);
         setFiles(selectedFiles);

         // Create previews for all selected files
         selectedFiles.forEach((file) => {
            const reader = new FileReader();
            reader.onloadend = () => {
               setPreviews((prev) => [...prev, reader.result as string]);
            };
            reader.readAsDataURL(file);
         });
      }
   };

   const handleUpload = async () => {
      if (files.length === 0) return;
      setUploading(true);
      try {
         const folderId =
            selectedFolder && selectedFolder !== "none"
               ? parseInt(selectedFolder)
               : undefined;
         await uploadImages(files, stageId, folderId);
         setFiles([]);
         setPreviews([]);
         setSelectedFolder("none");
      } catch (error) {
         console.error("Error uploading images:", error);
      } finally {
         setUploading(false);
      }
   };

   const removeFile = (index: number) => {
      setFiles((prev) => prev.filter((_, i) => i !== index));
      setPreviews((prev) => prev.filter((_, i) => i !== index));
   };

   const renderFolderOptions = (
      folders: FolderType[],
      level = 0
   ): JSX.Element[] => {
      return folders.flatMap((folder) => [
         <SelectItem key={folder.id} value={folder.id.toString()}>
            {"\u00A0".repeat(level * 2)}
            {folder.name}
         </SelectItem>,
         ...(folder.children
            ? renderFolderOptions(folder.children, level + 1)
            : []),
      ]);
   };

   return (
      <Card className="p-4">
         <CardHeader>
            <CardTitle>Upload Images</CardTitle>
         </CardHeader>
         <CardContent className="space-y-4">
            <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-gray-400 transition-colors">
               {previews.length > 0 ? (
                  <div className="grid grid-cols-2 gap-4 w-full">
                     {previews.map((preview, index) => (
                        <div key={index} className="relative">
                           <img
                              src={preview}
                              alt={`Preview ${index + 1}`}
                              className="rounded-lg shadow-sm"
                           />
                           <Button
                              variant="destructive"
                              size="icon"
                              className="absolute top-2 right-2"
                              onClick={() => removeFile(index)}
                           >
                              <Trash2 className="h-4 w-4" />
                           </Button>
                        </div>
                     ))}
                  </div>
               ) : (
                  <div className="text-center">
                     <Input
                        type="file"
                        onChange={handleFileChange}
                        accept="image/*"
                        multiple
                        className="hidden"
                        id="image-upload"
                     />
                     <label
                        htmlFor="image-upload"
                        className="cursor-pointer flex flex-col items-center"
                     >
                        <div className="p-4 rounded-full bg-gray-100 mb-2">
                           <Upload className="h-6 w-6 text-gray-600" />
                        </div>
                        <span className="text-sm text-gray-600">
                           Click to upload or drag and drop multiple images
                        </span>
                     </label>
                  </div>
               )}
            </div>

            <Select value={selectedFolder} onValueChange={setSelectedFolder}>
               <SelectTrigger>
                  <SelectValue placeholder="Select folder (optional)" />
               </SelectTrigger>
               <SelectContent>
                  <SelectItem value="none">No folder</SelectItem>
                  {renderFolderOptions(folders)}
               </SelectContent>
            </Select>

            <Button
               onClick={handleUpload}
               disabled={files.length === 0 || uploading}
               className="w-full"
            >
               {uploading ? (
                  <>
                     <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                     Uploading {files.length} image
                     {files.length !== 1 ? "s" : ""}...
                  </>
               ) : (
                  `Upload ${files.length} image${files.length !== 1 ? "s" : ""}`
               )}
            </Button>
         </CardContent>
      </Card>
   );
}
